import { useTranslation } from '@donaswap/localization'
import { ChainId, NATIVE } from '@donaswap/sdk'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  Box,
  Button,
  Flex,
  InfoIcon,
  Input,
  Text,
  UserMenu,
  UserMenuDivider,
  UserMenuItem,
  useTooltip,
} from '@donaswap/uikit'
import { useNetwork } from 'wagmi'
import { useActiveChainId, useLocalNetworkChain } from 'hooks/useActiveChainId'
import { useNetworkConnectorUpdater } from 'hooks/useActiveWeb3React'
import { useHover } from 'hooks/useHover'
import { useSessionChainId } from 'hooks/useSessionChainId'
import { useSwitchNetwork } from 'hooks/useSwitchNetwork'
import { useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { chains } from 'utils/wagmi'
import Image from 'next/image'
// import { ASSET_CDN } from 'config/constants/endpoints'

import { ChainLogo } from './Logo/ChainLogo'

const AptosChain = {
  id: 1,
  name: 'Aptos',
}

const SolanaChain = {
  id: 2,
  name: 'Solana',
}

const NetworkSelect = ({ switchNetwork, chainId }) => {
  const { t } = useTranslation()
  const [ filter, setFilter ] = useState("")

  return (
    <>
      <Box px="16px" py="8px">
        <Input
          style={{ color: 'inherit', backgroundColor: 'transparent' }}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setFilter(e.currentTarget.value)}
          placeholder={t('Select a Network')}
        />
        {/* <Text color="textSubtle">{t('Select a Network')}</Text> */}
      </Box>
      <UserMenuDivider />
      {chains
        // .filter(
        //   (chain) =>
        //     chain.id === ChainId.LINEA_TESTNET || !('testnet' in chain && chain.testnet) || chain.id === chainId,
        // )
        .filter((chain) => !filter || chain.name.toLowerCase().includes(filter.toLowerCase()))

        .map((chain) => (
          <UserMenuItem
            key={chain.id}
            style={{ justifyContent: 'flex-start' }}
            onClick={() => chain.id !== chainId && switchNetwork(chain.id)}
          >
            <ChainLogo chainId={chain.id} />
            <Text color={chain.id === chainId ? 'secondary' : 'text'} bold={chain.id === chainId} pl="12px">
              {chain.name}
            </Text>
          </UserMenuItem>
        ))}
      <UserMenuItem
        key={`aptos-${AptosChain.id}`}
        style={{ justifyContent: 'flex-start' }}
        as="a"
        target="_blank"
        href="https://interface-donaswap.vercel.app/swap"
      >
        <Image
          src="https://aptos.pancakeswap.finance/images/apt.png"
          width={24}
          height={24}
          unoptimized
          alt={`chain-aptos-${AptosChain.id}`}
        />
        <Text color="text" pl="12px">
          {AptosChain.name}
        </Text>
      </UserMenuItem>
      <UserMenuItem
        key={`solona-${SolanaChain.id}`}
        style={{ justifyContent: 'flex-start' }}
        as="a"
        target="_blank"
        href="https://solana-interface.vercel.app/swap"
      >
        <Image
          src="/images/chains/solana.png"
          width={24}
          height={24}
          unoptimized
          alt={`chain-solona-${SolanaChain.id}`}
        />
        <Text color="text" pl="12px">
          {SolanaChain.name}
        </Text>
      </UserMenuItem>
    </>
  )
}

const WrongNetworkSelect = ({ switchNetwork, chainId }) => {
  const { t } = useTranslation()
  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(
      'The URL you are accessing (Chain id: %chainId%) belongs to %network%; mismatching your wallet’s network. Please switch the network to continue.',
      {
        chainId,
        network: chains.find((c) => c.id === chainId)?.name ?? 'Unknown network',
      },
    ),
    {
      placement: 'auto-start',
      hideTimeout: 0,
    },
  )
  const { chain } = useNetwork()
  const localChainId = useLocalNetworkChain() || ChainId.BSC
  const [, setSessionChainId] = useSessionChainId()

  const localChainName = chains.find((c) => c.id === localChainId)?.name ?? 'BSC'

  const [ref1, isHover] = useHover<HTMLButtonElement>()

  return (
    <>
      <Flex ref={targetRef} alignItems="center" px="16px" py="8px">
        <InfoIcon color="textSubtle" />
        <Text color="textSubtle" pl="6px">
          {t('Please switch network')}
        </Text>
      </Flex>
      {tooltipVisible && tooltip}
      <UserMenuDivider />
      {chain && (
        <UserMenuItem ref={ref1} onClick={() => setSessionChainId(chain.id)} style={{ justifyContent: 'flex-start' }}>
          <ChainLogo chainId={chain.id} />
          <Text color="secondary" bold pl="12px">
            {chain.name}
          </Text>
        </UserMenuItem>
      )}
      <Box px="16px" pt="8px">
        {isHover ? <ArrowUpIcon color="text" /> : <ArrowDownIcon color="text" />}
      </Box>
      <UserMenuItem onClick={() => switchNetwork(localChainId)} style={{ justifyContent: 'flex-start' }}>
        <ChainLogo chainId={localChainId} />
        <Text pl="12px">{localChainName}</Text>
      </UserMenuItem>
      <Button mx="16px" my="8px" scale="sm" onClick={() => switchNetwork(localChainId)}>
        {t('Switch network in wallet')}
      </Button>
    </>
  )
}

const SHORT_SYMBOL = {
  [ChainId.FIRECHAIN]: 'FIRE',
  [ChainId.RINIA]: 'tFIRE',
  [ChainId.ETHEREUM]: 'ETH',
  [ChainId.GOERLI]: 'GOR',
  [ChainId.SEPOLIA]: 'SEP',
  [ChainId.BSC]: 'BNB',
  [ChainId.BSC_TESTNET]: 'tBNB',
  [ChainId.POLYGON]: 'MATIC',
  [ChainId.POLYGON_MUMBAI]: 'tMATIC',
  [ChainId.POLYGON_ZKEVM]: 'Polygon zkEVM',
  [ChainId.POLYGON_ZKEVM_TESTNET]: 'tZkEVM',
  [ChainId.CRO]: 'CRO',
  [ChainId.CRO_TESTNET]: 'tCRO',
  [ChainId.AVALANCHE]: 'AVAX',
  [ChainId.AVALANCHE_FUJI]: 'tAVAX',
  [ChainId.FANTOM_OPERA]: 'FTM',
  [ChainId.FANTOM_TESTNET]: 'tFTM',
  [ChainId.CELO]: 'CELO',
  [ChainId.CELO_ALFAJORES]: 'tCELO',
  [ChainId.OPTIMISM]: 'OP',
  [ChainId.OPTIMISM_GOERLI]: 'tOP',
  [ChainId.SHIBARIUM]: '',
  [ChainId.SHIBARIUM_TESTNET]: '',
  [ChainId.PULSECHAIN]: '',
  [ChainId.PULSECHAIN_TESTNET]: '',
  [ChainId.XEN]: '',
  [ChainId.XEN_DEVNET]: '',
  [ChainId.HARMONY]: '',
  [ChainId.HARMONY_TESTNET]: '',
  [ChainId.COINEX]: '',
  [ChainId.COINEX_TESTNET]: '',
  [ChainId.DOGECHAIN]: '',
  [ChainId.DOGECHAIN_TESTNET]: '',
  [ChainId.ETHEREUM_CLASSIC]: '',
  [ChainId.ETHEREUM_CLASSIC_TESTNET]: '',
  [ChainId.FUSION]: '',
  [ChainId.FUSION_TESTNET]: '',
  [ChainId.HECO]: '',
  [ChainId.HECO_TESTNET]: '',
  [ChainId.KCC]: '',
  [ChainId.KCC_TESTNET]: '',
  [ChainId.KLAYTN]: '',
  [ChainId.KLAYTN_BAOBAB]: '',
  [ChainId.OKXCHAIN]: '',
  [ChainId.OKXCHAIN_TESTNET]: '',
  [ChainId.THETHA]: '',
  [ChainId.THETHA_TESTNET]: '',
  [ChainId.ULTRON]: '',
  [ChainId.ULTRON_TESTNET]: '',
  [ChainId.MOONBEAM]: '',
  [ChainId.MOONRIVER]: '',
  [ChainId.MOONBASE_ALPHA]: '',
  [ChainId.AURORA]: '',
  [ChainId.AURORA_TESTNET]: '',
  [ChainId.BOBA]: '',
  [ChainId.BOBA_GOERLI]: '',
  [ChainId.GNOSIS]: '',
  [ChainId.GNOSIS_CHIADO]: '',
  [ChainId.METIS]: '',
  [ChainId.METIS_GOERLI]: '',
  [ChainId.TELOS]: '',
  [ChainId.TELOS_TESTNET]: '',
  [ChainId.SYSCOIN]: '',
  [ChainId.SYSCOIN_TANENBAUM]: '',
  [ChainId.IOTEX]: '',
  [ChainId.IOTEX_TESTNET]: '',
  [ChainId.SHIDEN]: '',
  [ChainId.ASTAR]: '',
  [ChainId.SHIBUNYA]: '',
  [ChainId.FUSE]: '',
  [ChainId.FUSE_SPARKNET]: '',
  [ChainId.VELAS]: '',
  [ChainId.VELAS_TESTNET]: '',
  [ChainId.REI]: '',
  [ChainId.REI_TESTNET]: '',
  [ChainId.KEKCHAIN]: '',
  [ChainId.KEKCHAIN_TESTNET]: '',
  [ChainId.TOMOCHAIN]: '',
  [ChainId.TOMOCHAIN_TESTNET]: '',
  [ChainId.THUNDERCORE]: '',
  [ChainId.THUNDERCORE_TESTNET]: '',
  [ChainId.WANCHAIN]: '',
  [ChainId.WANCHAIN_TESTNET]: '',
  [ChainId.RSK]: '',
  [ChainId.RSK_TESTNET]: '',
  [ChainId.ELASTOS]: '',
  [ChainId.ELASTOS_TESTNET]: '',
  [ChainId.CONFLUX]: '',
  [ChainId.CONFLUX_TESTNET]: '',
  [ChainId.BRISECHAIN]: '',
  [ChainId.BRISECHAIN_TESTNET]: '',
  [ChainId.MUUCHAIN]: '',
  [ChainId.MUUCHAIN_TESTNET]: '',
  [ChainId.CANTO]: '',
  [ChainId.CANTO_TESTNET]: '',
  [ChainId.OASIS_EMERALD]: '',
  [ChainId.OASIS_TESTNET]: '',
  [ChainId.VISION]: '',
  [ChainId.VISION_TESTNET]: '',
  [ChainId.STEP]: '',
  [ChainId.STEP_TESTNET]: '',
  [ChainId.METER]: '',
  [ChainId.METER_TESTNET]: '',
  [ChainId.GODWOKEN]: '',
  [ChainId.GODWOKEN_TESTNET]: '',
  [ChainId.CALLISTO]: '',
  [ChainId.CALLISTO_TESTNET]: '',
  [ChainId.EVMOS]: '',
  [ChainId.EVMOS_TESTNET]: '',
  [ChainId.ENERGY_WEB_CHAIN]: '',
  [ChainId.ENERGY_VOLTA]: '',
  [ChainId.BASE]: '',
  [ChainId.BASE_GOERLI]: '',
  [ChainId.KAVA]: '',
  [ChainId.KAVA_TESTNET]: '',
  [ChainId.CLOVER]: '',
  [ChainId.CLOVER_TESTNET]: '',
  [ChainId.DEFICHAIN]: '',
  [ChainId.DEFICHAIN_TESTNET]: '',
  [ChainId.BRONOS]: '',
  [ChainId.BRONOS_TESTNET]: '',
  [ChainId.FILECOIN]: '',
  [ChainId.FILECOIN_CALIBRATION]: '',
  [ChainId.FLARE]: '',
  [ChainId.FLARE_TESTNET]: '',
  [ChainId.TARAXA]: '',
  [ChainId.TARAXA_TESTNET]: '',

  [ChainId.ARBITRUM_ONE]: 'ARB',
  [ChainId.ARBITRUM_GOERLI]: 'tARB',
  [ChainId.ZKSYNC]: 'zkSync',
  [ChainId.ZKSYNC_TESTNET]: 'tZkSync',
  [ChainId.LINEA_TESTNET]: 'tLinea',
} as const satisfies Record<ChainId, string>

export const NetworkSwitcher = () => {
  const { t } = useTranslation()
  const { chainId, isWrongNetwork, isNotMatched } = useActiveChainId()
  const { pendingChainId, isLoading, canSwitch, switchNetworkAsync } = useSwitchNetwork()
  const router = useRouter()

  useNetworkConnectorUpdater()

  const foundChain = useMemo(
    () => chains.find((c) => c.id === (isLoading ? pendingChainId || chainId : chainId)),
    [isLoading, pendingChainId, chainId],
  )
  const symbol = SHORT_SYMBOL[foundChain?.id] ?? NATIVE[foundChain?.id]?.symbol ?? foundChain?.nativeCurrency?.symbol
  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t('Unable to switch network. Please try it on your wallet'),
    { placement: 'bottom' },
  )

  const cannotChangeNetwork = !canSwitch

  if (!chainId || router.pathname.includes('/info')) {
    return null
  }

  return (
    <Box ref={cannotChangeNetwork ? targetRef : null} height="100%">
      {cannotChangeNetwork && tooltipVisible && tooltip}
      <UserMenu
        mr="8px"
        placement="bottom"
        variant={isLoading ? 'pending' : isWrongNetwork ? 'danger' : 'default'}
        // avatarSrc={`${ASSET_CDN}/web/chains/${chainId}.png`}
        avatarSrc={`/images/chains/${chainId}.png`}
        disabled={cannotChangeNetwork}
        text={
          isLoading ? (
            t('Requesting')
          ) : isWrongNetwork ? (
            t('Network')
          ) : foundChain ? (
            <>
              <Box display={['none', null, null, null, null, 'block']}>{foundChain.name}</Box>
              <Box display={['block', null, null, null, null, 'none']}>{symbol}</Box>
            </>
          ) : (
            t('Select a Network')
          )
        }
      >
        {() =>
          isNotMatched ? (
            <WrongNetworkSelect switchNetwork={switchNetworkAsync} chainId={chainId} />
          ) : (
            <div style={{ maxHeight: '360px', overflow: 'scroll', overflowX: 'hidden' }}>
            <NetworkSelect switchNetwork={switchNetworkAsync} chainId={chainId} />
            </div>
          )
        }
      </UserMenu>
    </Box>
  )
}
